/**
 * Offers API Slice
 * Injects offers endpoints into the base API slice
 */

import { apiSlice } from "../api/apiSlice";
import { Offer } from "../../types/offers";
import { generateListCacheTag, CACHE_TIME } from "../api/apiHelpers";
import { mallsApiSlice } from "../malls/mallsApiSlice";

// Backend response structure
interface OffersBackendResponse {
  success: boolean;
  data: Offer[]; // Array of offers directly
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  stats: {
    total: number;
    expiringSoon: number;
    newOffers: number;
  };
}

// Backend response for single offer
interface OfferBackendResponse {
  success: boolean;
  data: Offer;
}

// Frontend expected structure
export interface OffersResponse {
  offers: Offer[];
  total: number;
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  stats: {
    total: number;
    expiringSoon: number;
    newOffers: number;
  };
}

export interface OfferFiltersParams {
  mallId?: string | undefined;
  category?: string;
  sortBy?: string;
  searchQuery?: string;
  page?: number;
  limit?: number;
}

export const offersApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Get offers for a mall or all featured offers
     * - mallId="all" → /api/offers/all (featured offers)
     * - mallId=specific → /api/offers/mall/:mallId (mall-specific offers)
     */
    getOffersByMall: builder.query<OffersResponse, OfferFiltersParams>({
      query: ({
        mallId,
        category,
        sortBy,
        searchQuery,
        page = 1,
        limit = 10,
      }) => {
        // Determine endpoint based on mallId
        const endpoint =
          mallId === "all" ? "/api/offers/all" : `/api/offers/mall/${mallId}`;

        return {
          url: endpoint,
          params: {
            category: category !== "all" ? category : undefined,
            sortBy,
            search: searchQuery,
            page,
            limit,
          },
        };
      },
      transformResponse: (response: OffersBackendResponse) => {
        // Transform backend response to frontend format
        return {
          offers: response.data || [],
          total: response.pagination?.total || 0,
          pagination: response.pagination,
          stats: response.stats || {
            total: 0,
            expiringSoon: 0,
            newOffers: 0,
          },
        };
      },
      providesTags: (result, error, { mallId, category, sortBy }) => [
        { type: "Offers", id: "LIST" },
        {
          type: "Offers",
          id: generateListCacheTag(
            mallId === "all" ? "ALL" : `MALL-${mallId}`,
            category || "all",
            sortBy || "newest"
          ),
        },
      ],
      keepUnusedDataFor: CACHE_TIME.VERY_LONG, // Offers don't change frequently - 30 min cache
    }),

    /**
     * Get offer by ID
     * Returns detailed information about a specific offer
     */
    getOfferById: builder.query<Offer, string>({
      query: (offerId) => ({
        url: `/api/offers/${offerId}`,
      }),
      transformResponse: (response: OfferBackendResponse) => {
        // Transform backend response to frontend format
        return response.data;
      },
      async onCacheEntryAdded(
        offerId,
        { cacheDataLoaded, cacheEntryRemoved, dispatch, getState }
      ) {
        try {
          // Wait for the data to be loaded
          const { data: offer } = await cacheDataLoaded;

          // Update all getOffersByMall cache entries with the updated view count
          // Backend auto-increments view count, so we sync it to offers lists
          const state: any = getState();
          const cacheEntries = state.api?.queries || {};

          Object.keys(cacheEntries).forEach((cacheKey) => {
            if (cacheKey.startsWith("getOffersByMall")) {
              const entry = cacheEntries[cacheKey];
              const mallId = entry?.originalArgs?.mallId;

              if (mallId && offer.mallId === mallId) {
                try {
                  dispatch(
                    offersApiSlice.util.updateQueryData(
                      "getOffersByMall",
                      entry.originalArgs,
                      (draft) => {
                        const cachedOffer = draft.offers.find(
                          (o: Offer) => o.id === offerId
                        );
                        if (cachedOffer) {
                          cachedOffer.viewCount = offer.viewCount;
                        }
                      }
                    )
                  );
                } catch (e) {
                  // Ignore individual cache update errors
                }
              }
            }
          });

          await cacheEntryRemoved;
        } catch {
          // Ignore errors during cache lifecycle
        }
      },
      providesTags: (result, error, offerId) => [
        { type: "Offers", id: offerId },
      ],
      keepUnusedDataFor: CACHE_TIME.VERY_LONG, // Offer details don't change frequently - 30 min cache
    }),

    /**
     * Increment offer view count
     * Called when user views offer details
     */
    incrementOfferView: builder.mutation<
      void,
      { offerId: string; mallId: string }
    >({
      query: ({ offerId }) => ({
        url: `/api/offers/${offerId}/view`,
        method: "POST",
      }),
      async onQueryStarted(
        { offerId, mallId },
        { dispatch, queryFulfilled, getState }
      ) {
        const patches: any[] = [];

        // Optimistically update MallHub cache
        try {
          const mallHubPatch = dispatch(
            mallsApiSlice.util.updateQueryData(
              "getMallHub",
              mallId,
              (draft) => {
                const mallHubDraft = draft as any;
                const offer = mallHubDraft.offers?.find(
                  (o: Offer) => o.id === offerId
                );
                if (offer) {
                  offer.viewCount = (offer.viewCount || 0) + 1;
                }
              }
            )
          );
          patches.push(mallHubPatch);
        } catch (e) {
          // Ignore if cache entry doesn't exist
        }

        // Optimistically update ALL OffersByMall cache entries for this mall
        const state: any = getState();
        const cacheEntries = state.api?.queries || {};

        Object.keys(cacheEntries).forEach((cacheKey) => {
          if (cacheKey.startsWith("getOffersByMall")) {
            const entry = cacheEntries[cacheKey];
            // Check if this cache entry is for our mallId
            if (entry?.originalArgs?.mallId === mallId) {
              try {
                const patch = dispatch(
                  offersApiSlice.util.updateQueryData(
                    "getOffersByMall",
                    entry.originalArgs,
                    (draft) => {
                      const offer = draft.offers.find(
                        (o: Offer) => o.id === offerId
                      );
                      if (offer) {
                        offer.viewCount = (offer.viewCount || 0) + 1;
                      }
                    }
                  )
                );
                patches.push(patch);
              } catch (e) {
                // Ignore errors for individual patches
              }
            }
          }
        });

        try {
          await queryFulfilled;
        } catch {
          // Rollback all optimistic updates on error
          patches.forEach((patch) => patch.undo());
        }
      },
      invalidatesTags: (result, error, { offerId }) => [
        { type: "Offers", id: offerId },
      ],
    }),

    /**
     * Increment offer share count
     * Called when user shares an offer
     */
    incrementOfferShare: builder.mutation<void, string>({
      query: (offerId) => ({
        url: `/api/offers/${offerId}/share`,
        method: "POST",
      }),
      // Only invalidate list cache to update cards, not individual item to avoid refetch
      invalidatesTags: () => [{ type: "Offers", id: "LIST" }],
    }),
  }),
});

export const {
  useGetOffersByMallQuery,
  useGetOfferByIdQuery,
  useIncrementOfferViewMutation,
  useIncrementOfferShareMutation,
} = offersApiSlice;
